/**
 * API Client with Interceptor Pattern
 * 
 * Features:
 * ✓ Browser sends HttpOnly cookies automatically (credentials: 'include')
 * ✓ 3 retries with exponential backoff (1s, 2s, 4s delays) for failed requests
 * ✓ 3 retries on 401/403 auth failures, then logout after exhaustion
 * ✓ Error notifications shown to user after retry exhaustion
 * ✓ S3 calls are not handled as it has auto headers  
 * ✓ Configurable via RETRY_CONFIG in constants/apiConfig.js
 */

import { retryWithBackoff } from './apiRetry';
import { RETRY_CONFIG } from '../constants/apiConfig';
import store from '../redux/store';
import { addErrorNotification } from '../redux/slices/notificationSlice';
import logger from './logger';

const API_BASE_PATH = process.env.REACT_APP_BASE_URL;

/**
 * Builds HTTP headers.
 * No longer injects Authorization header — browser sends cookies automatically.
 */
const buildHeaders = (extraHeaders = {}, contentType = 'application/json') => {
    const headers = {
        accept: 'application/json',
        ...extraHeaders,
    };

    if (contentType && !headers['Content-Type']) {
        headers['Content-Type'] = contentType;
    }

    // REMOVED: No more Authorization header injection
    // Tokens are sent as HttpOnly cookies by the browser automatically

    return new Headers(headers);
};

const isServerError = (status) => status >= 500 && status < 600;
const isAuthError = (status) => status === 401 || status === 403;

const handleUnauthorized = async (status) => {
    logger.warn(`Authentication failed with status ${status} after ${RETRY_CONFIG.maxRetries} retries. Triggering logout.`);
    try {
        const { signOut } = await import('../redux/slices/authSlice');
        store.dispatch(signOut());
    } catch (e) {
        console.error('Failed to dispatch signOut after auth failure:', e);
    }
};

const parseErrorResponse = async (response) => {
    try {
        const data = await response.json();
        if (data.detail && data.detail.message) {
            return { message: data.detail.message, detail: data.detail };
        }
        if (Array.isArray(data.messages) && data.messages.length > 0) {
            return { message: data.messages[0], detail: null };
        }
        if (data.message) {
            return { message: data.message, detail: null };
        }
    } catch (e) {
        // Ignore JSON parse errors
    }
    return { message: `HTTP ${response.status}: ${response.statusText}`, detail: null };
};

export const apiFetch = async (pathOrUrl, options = {}) => {
    const {
        method = 'GET',
        headers = {},
        body,
        signal,
        retry = true,
        contentType = 'application/json',
        ...rest
    } = options;

    const url = pathOrUrl.startsWith('http') ? pathOrUrl : `${API_BASE_PATH}${pathOrUrl}`;

    const doRequest = async () => {
        const response = await fetch(url, {
            method,
            headers: buildHeaders(headers, contentType),
            body,
            signal,
            credentials: 'include', // Browser sends cookies automatically
            ...rest,
        });

        if (!response.ok) {
            const parsed = await parseErrorResponse(response);
            const error = new Error(parsed.message);
            error.status = response.status;
            error.detail = parsed.detail;
            if (isServerError(response.status) || isAuthError(response.status)) {
                error.isRetryable = !!retry;
            } else {
                error.isRetryable = false;
            }
            throw error;
        }

        if (response.status === 204) {
            return 'No Content';
        }

        return response.json();
    };

    try {
        if (retry) {
            return await retryWithBackoff(doRequest);
        }
        return await doRequest();
    } catch (error) {
        if (retry) {
            logger.error(`API call to ${url} failed after ${RETRY_CONFIG.maxRetries} retries.`, error.message);

            if (isAuthError(error.status)) {
                handleUnauthorized(error.status);
            }

            store.dispatch(addErrorNotification({
                message: `API request failed: ${error.message || 'Please try again later'}`,
                isInline: false
            }));
        }
        throw error;
    }
};

export const callWithAuthRetry = async (makeRequest) => {
    const doRequest = async () => {
        const response = await makeRequest();
        const { status, ok } = response;

        if (isAuthError(status) || (!ok && isServerError(status))) {
            const error = new Error(`HTTP ${status}`);
            error.status = status;
            error.isRetryable = true;
            throw error;
        }

        return response;
    };

    try {
        return await retryWithBackoff(doRequest);
    } catch (error) {
        const status = error?.status;

        if (status && (isAuthError(status) || isServerError(status))) {
            if (isAuthError(status)) {
                handleUnauthorized(status);
            }

            logger.error(`API call failed after retries with status ${status}.`, error.message);

            store.dispatch(addErrorNotification({
                message: `${isAuthError(status) ? 'Authentication' : 'Server'} error: ${error.message || 'Please try again later'}`,
                isInline: false
            }));
        }
        throw error;
    }
};